// Mock data for Compliance Workload page
//
// This page shows actionable open findings prioritized by SLA urgency.
// The goal is to help users see what work needs to be done and when.

// ============================================================================
// Types
// ============================================================================

export type ScanType = 'semgrep' | 'secrets' | 'deep_ai_scan';
export type Severity = 'critical' | 'high' | 'medium' | 'low';
export type Urgency = 'breached' | 'due_today' | 'due_tomorrow' | 'due_this_week' | 'healthy';
export type AssessedPriority = 'High' | 'Medium' | 'Low';

/**
 * Page-level data structure for the Workload page.
 *
 * This would be returned by a new API endpoint like:
 * GET /organizations/{org_id}/compliance/workload
 *
 * The backend would compute SLA deadlines using:
 * - FindingEvents (detection timestamps)
 * - SlaConfig (rules with target_business_days)
 * - BusinessCalendar (working days, holidays)
 */
export interface WorkloadPageData {
  organizationId: string;
  window: {
    start: string;  // ISO date
    end: string;    // ISO date
  };
  scope: {
    applicationIds: string[] | null;  // null = all
    applicationCount: number;
    repositoryCount: number;
  };

  // Summary metrics
  summary: {
    totalOpen: number;
    breached: number;
    dueSoon: number;      // Due within 3 business days (includes due_today, due_tomorrow)
    healthy: number;      // More than 3 days remaining
  };

  // Detailed breakdown by urgency for stats cards
  byUrgency: {
    breached: number;
    dueToday: number;
    dueTomorrow: number;
    dueThisWeek: number;  // 2-5 days
    healthy: number;      // 5+ days
  };

  // Breakdown by severity (within open findings)
  bySeverity: {
    error: { total: number; breached: number; };
    warning: { total: number; breached: number; };
    info: { total: number; breached: number; };
  };

  // Breakdown by scan type
  byScanType: {
    semgrep: { total: number; breached: number; };
    secrets: { total: number; breached: number; };
    deep_ai_scan: { total: number; breached: number; };
  };

  // List of applications with their workload
  applications: ApplicationWorkload[];

  // The actual findings sorted by urgency (most urgent first)
  findings: FindingWorkloadItem[];

  // Calendar view - findings grouped by deadline date (next 14 days)
  calendar: CalendarDay[];
}

/**
 * Application-level workload summary
 */
export interface ApplicationWorkload {
  id: string;
  name: string;
  totalOpen: number;
  breached: number;
  dueSoon: number;
  healthy: number;
}

/**
 * Individual finding with SLA/urgency context.
 *
 * This combines data from:
 * - Finding model (core finding data)
 * - Computed SLA deadline (from SlaConfig + BusinessCalendar)
 * - Application name (joined from applications collection)
 */
export interface FindingWorkloadItem {
  // Core finding info
  id: string;
  checkId: string;
  ruleName: string;           // Human-readable rule name (derived from check_id)
  message: string;            // Finding description
  filePath: string;
  severity: Severity;
  scanType: ScanType;

  // Application context
  applicationId: string;
  applicationName: string;

  // SLA tracking (computed)
  detectedAt: string;         // ISO datetime - from first_detected_at
  slaDeadline: string;        // ISO datetime - computed: detected + target_business_days
  slaDays: number;            // Target from SLA rule (e.g., 5 days for semgrep error)

  // Urgency (computed from current date vs deadline)
  daysRemaining: number;      // Negative = overdue, 0 = due today
  urgency: Urgency;

  // Assessment info (if available)
  assessedPriority?: AssessedPriority;
  isRealConcern?: string;     // 'Yes' | 'Yes, but low impact' | 'No' | 'Unclear'
}

/**
 * Calendar day entry for timeline/calendar view.
 * Shows what's due on each day.
 */
export interface CalendarDay {
  date: string;               // YYYY-MM-DD
  dayOfWeek: string;          // 'Mon', 'Tue', etc.
  isToday: boolean;
  isPast: boolean;            // For overdue items
  isWeekend: boolean;
  findings: {
    total: number;
    bySeverity: {
      error: number;
      warning: number;
      info: number;
    };
  };
}

// ============================================================================
// SLA Configuration Reference (for context)
// ============================================================================

/**
 * This reflects the SLA rules that would be configured in the org settings.
 * Used here for reference when generating mock data.
 */
export const slaRulesReference = {
  semgrep: { error: 5, warning: 14, info: 30 },       // business days
  secrets: { error: 1, warning: 7, info: 14 },
  deep_ai_scan: { error: 10, warning: 21, info: 45 },
};

// ============================================================================
// Mock Data
// ============================================================================

export const workloadMockData: WorkloadPageData = {
  organizationId: 'org-d5b7ce01-ad5b-4c35',
  window: {
    start: '2024-01-01',
    end: '2024-01-31',
  },
  scope: {
    applicationIds: null,
    applicationCount: 6,
    repositoryCount: 9,
  },

  summary: {
    totalOpen: 47,
    breached: 8,
    dueSoon: 12,    // due_today + due_tomorrow + due_this_week (within 5 days)
    healthy: 27,
  },

  byUrgency: {
    breached: 8,
    dueToday: 3,
    dueTomorrow: 4,
    dueThisWeek: 5,   // 2-5 days
    healthy: 27,      // 5+ days
  },

  bySeverity: {
    error: { total: 18, breached: 5 },
    warning: { total: 21, breached: 3 },
    info: { total: 8, breached: 0 },
  },

  byScanType: {
    semgrep: { total: 22, breached: 4 },
    secrets: { total: 12, breached: 3 },
    deep_ai_scan: { total: 13, breached: 1 },
  },

  applications: [
    { id: 'app-azuro', name: 'Azuro Protocol', totalOpen: 14, breached: 4, dueSoon: 5, healthy: 5 },
    { id: 'app-onekey', name: 'OneKey Wallet', totalOpen: 15, breached: 3, dueSoon: 4, healthy: 8 },
    { id: 'app-ghostfolio', name: 'Ghostfolio', totalOpen: 8, breached: 1, dueSoon: 2, healthy: 5 },
    { id: 'app-nautilus', name: 'NautilusTrader', totalOpen: 5, breached: 0, dueSoon: 1, healthy: 4 },
    { id: 'app-killbill', name: 'Kill Bill', totalOpen: 3, breached: 0, dueSoon: 0, healthy: 3 },
    { id: 'app-stash', name: 'Stash', totalOpen: 2, breached: 0, dueSoon: 0, healthy: 2 },
  ],

  findings: [
    // ========================================================================
    // BREACHED FINDINGS (8 total) - Most urgent, sorted by days overdue
    // ========================================================================
    {
      id: 'find-001',
      checkId: 'generic.secrets.security.detected-aws-secret-access-key',
      ruleName: 'AWS Secret Access Key Detected',
      message: 'AWS secret access key detected in source code. This could lead to unauthorized access to AWS services.',
      filePath: 'src/config/aws-credentials.ts',
      severity: 'high',
      scanType: 'secrets',
      applicationId: 'app-azuro',
      applicationName: 'Azuro Protocol',
      detectedAt: '2024-01-08T10:30:00Z',
      slaDeadline: '2024-01-09T10:30:00Z',
      slaDays: 1,
      daysRemaining: -6,
      urgency: 'breached',
      assessedPriority: 'High',
      isRealConcern: 'Yes',
    },
    {
      id: 'find-002',
      checkId: 'python.lang.security.audit.dangerous-subprocess-use',
      ruleName: 'Dangerous Subprocess Usage',
      message: 'Subprocess call with shell=True could allow command injection if user input is not properly sanitized.',
      filePath: 'src/utils/shell_executor.py',
      severity: 'high',
      scanType: 'semgrep',
      applicationId: 'app-azuro',
      applicationName: 'Azuro Protocol',
      detectedAt: '2024-01-05T14:20:00Z',
      slaDeadline: '2024-01-12T14:20:00Z',
      slaDays: 5,
      daysRemaining: -4,
      urgency: 'breached',
      assessedPriority: 'High',
    },
    {
      id: 'find-003',
      checkId: 'generic.secrets.security.detected-github-token',
      ruleName: 'GitHub Token Exposed',
      message: 'GitHub personal access token found in source code.',
      filePath: '.github/workflows/deploy.yml',
      severity: 'high',
      scanType: 'secrets',
      applicationId: 'app-onekey',
      applicationName: 'OneKey Wallet',
      detectedAt: '2024-01-10T09:00:00Z',
      slaDeadline: '2024-01-11T09:00:00Z',
      slaDays: 1,
      daysRemaining: -5,
      urgency: 'breached',
      assessedPriority: 'High',
      isRealConcern: 'Yes',
    },
    {
      id: 'find-004',
      checkId: 'javascript.express.security.audit.xss-serialize-javascript',
      ruleName: 'XSS via Serialize JavaScript',
      message: 'User-controlled data passed to serialize() without sanitization may lead to XSS.',
      filePath: 'src/api/handlers/render.js',
      severity: 'high',
      scanType: 'semgrep',
      applicationId: 'app-azuro',
      applicationName: 'Azuro Protocol',
      detectedAt: '2024-01-08T11:00:00Z',
      slaDeadline: '2024-01-15T11:00:00Z',
      slaDays: 5,
      daysRemaining: -1,
      urgency: 'breached',
    },
    {
      id: 'find-005',
      checkId: 'ai-security-review.sql-injection-risk',
      ruleName: 'Potential SQL Injection',
      message: 'String concatenation used in SQL query construction. Consider using parameterized queries.',
      filePath: 'src/database/queries.py',
      severity: 'high',
      scanType: 'deep_ai_scan',
      applicationId: 'app-onekey',
      applicationName: 'OneKey Wallet',
      detectedAt: '2024-01-06T16:45:00Z',
      slaDeadline: '2024-01-15T16:45:00Z',
      slaDays: 7,
      daysRemaining: -1,
      urgency: 'breached',
      assessedPriority: 'Medium',
      isRealConcern: 'Yes, but low impact',
    },
    {
      id: 'find-006',
      checkId: 'python.lang.security.audit.hardcoded-password',
      ruleName: 'Hardcoded Password',
      message: 'Hardcoded password found in source code. Store secrets securely.',
      filePath: 'tests/fixtures/test_auth.py',
      severity: 'medium',
      scanType: 'semgrep',
      applicationId: 'app-azuro',
      applicationName: 'Azuro Protocol',
      detectedAt: '2024-01-02T08:00:00Z',
      slaDeadline: '2024-01-16T08:00:00Z',
      slaDays: 14,
      daysRemaining: -1,
      urgency: 'breached',
      assessedPriority: 'Low',
      isRealConcern: 'Yes, but low impact',
    },
    {
      id: 'find-007',
      checkId: 'generic.secrets.security.detected-private-key',
      ruleName: 'Private Key Detected',
      message: 'RSA private key detected in source code.',
      filePath: 'config/ssl/server.key',
      severity: 'high',
      scanType: 'secrets',
      applicationId: 'app-ghostfolio',
      applicationName: 'Ghostfolio',
      detectedAt: '2024-01-12T14:00:00Z',
      slaDeadline: '2024-01-15T14:00:00Z',
      slaDays: 1,
      daysRemaining: -1,
      urgency: 'breached',
      assessedPriority: 'High',
    },
    {
      id: 'find-008',
      checkId: 'typescript.react.security.audit.react-dangerouslysetinnerhtml',
      ruleName: 'Dangerous innerHTML Usage',
      message: 'Using dangerouslySetInnerHTML with user-controlled data may lead to XSS.',
      filePath: 'src/components/RichText.tsx',
      severity: 'medium',
      scanType: 'semgrep',
      applicationId: 'app-onekey',
      applicationName: 'OneKey Wallet',
      detectedAt: '2024-01-04T10:30:00Z',
      slaDeadline: '2024-01-18T10:30:00Z',
      slaDays: 14,
      daysRemaining: -2,
      urgency: 'breached',
    },

    // ========================================================================
    // DUE TODAY (3 total)
    // ========================================================================
    {
      id: 'find-009',
      checkId: 'python.django.security.audit.csrf-disabled',
      ruleName: 'CSRF Protection Disabled',
      message: 'CSRF protection is disabled for this view. This may allow CSRF attacks.',
      filePath: 'src/views/api_views.py',
      severity: 'high',
      scanType: 'semgrep',
      applicationId: 'app-nautilus',
      applicationName: 'NautilusTrader',
      detectedAt: '2024-01-11T09:00:00Z',
      slaDeadline: '2024-01-18T09:00:00Z',
      slaDays: 5,
      daysRemaining: 0,
      urgency: 'due_today',
      assessedPriority: 'Medium',
    },
    {
      id: 'find-010',
      checkId: 'generic.secrets.security.detected-jwt-secret',
      ruleName: 'JWT Secret Exposed',
      message: 'JWT secret key found hardcoded in source.',
      filePath: 'src/auth/jwt_config.ts',
      severity: 'high',
      scanType: 'secrets',
      applicationId: 'app-ghostfolio',
      applicationName: 'Ghostfolio',
      detectedAt: '2024-01-17T11:00:00Z',
      slaDeadline: '2024-01-18T11:00:00Z',
      slaDays: 1,
      daysRemaining: 0,
      urgency: 'due_today',
      assessedPriority: 'High',
    },
    {
      id: 'find-011',
      checkId: 'javascript.browser.security.insecure-document-method',
      ruleName: 'Insecure Document Method',
      message: 'document.write() can lead to XSS if used with user input.',
      filePath: 'src/legacy/widget.js',
      severity: 'medium',
      scanType: 'semgrep',
      applicationId: 'app-azuro',
      applicationName: 'Azuro Protocol',
      detectedAt: '2024-01-04T15:00:00Z',
      slaDeadline: '2024-01-18T15:00:00Z',
      slaDays: 14,
      daysRemaining: 0,
      urgency: 'due_today',
    },

    // ========================================================================
    // DUE TOMORROW (4 total)
    // ========================================================================
    {
      id: 'find-012',
      checkId: 'python.lang.security.audit.insecure-hash-md5',
      ruleName: 'Insecure MD5 Hash',
      message: 'MD5 is cryptographically weak. Use SHA-256 or stronger.',
      filePath: 'src/utils/hashing.py',
      severity: 'medium',
      scanType: 'semgrep',
      applicationId: 'app-onekey',
      applicationName: 'OneKey Wallet',
      detectedAt: '2024-01-05T08:00:00Z',
      slaDeadline: '2024-01-19T08:00:00Z',
      slaDays: 14,
      daysRemaining: 1,
      urgency: 'due_tomorrow',
    },
    {
      id: 'find-013',
      checkId: 'ai-security-review.path-traversal-risk',
      ruleName: 'Path Traversal Risk',
      message: 'File path constructed from user input without sanitization.',
      filePath: 'src/api/file_handler.py',
      severity: 'high',
      scanType: 'deep_ai_scan',
      applicationId: 'app-ghostfolio',
      applicationName: 'Ghostfolio',
      detectedAt: '2024-01-12T10:00:00Z',
      slaDeadline: '2024-01-19T10:00:00Z',
      slaDays: 7,
      daysRemaining: 1,
      urgency: 'due_tomorrow',
      assessedPriority: 'Medium',
    },
    {
      id: 'find-014',
      checkId: 'generic.secrets.security.detected-api-key',
      ruleName: 'API Key Exposed',
      message: 'Generic API key detected in source code.',
      filePath: 'src/integrations/stripe.ts',
      severity: 'medium',
      scanType: 'secrets',
      applicationId: 'app-azuro',
      applicationName: 'Azuro Protocol',
      detectedAt: '2024-01-12T14:00:00Z',
      slaDeadline: '2024-01-19T14:00:00Z',
      slaDays: 7,
      daysRemaining: 1,
      urgency: 'due_tomorrow',
    },
    {
      id: 'find-015',
      checkId: 'typescript.react.security.audit.react-no-refs-on-function-components',
      ruleName: 'Invalid Ref Usage',
      message: 'Function components cannot be given refs. Use forwardRef instead.',
      filePath: 'src/components/Modal.tsx',
      severity: 'low',
      scanType: 'semgrep',
      applicationId: 'app-onekey',
      applicationName: 'OneKey Wallet',
      detectedAt: '2024-01-04T09:00:00Z',
      slaDeadline: '2024-01-19T09:00:00Z',
      slaDays: 15,
      daysRemaining: 1,
      urgency: 'due_tomorrow',
    },

    // ========================================================================
    // DUE THIS WEEK (5 total) - 2-5 days remaining
    // ========================================================================
    {
      id: 'find-016',
      checkId: 'python.lang.security.audit.eval-detected',
      ruleName: 'Eval Usage Detected',
      message: 'eval() can execute arbitrary code. Avoid or sanitize input.',
      filePath: 'src/scripting/evaluator.py',
      severity: 'high',
      scanType: 'semgrep',
      applicationId: 'app-nautilus',
      applicationName: 'NautilusTrader',
      detectedAt: '2024-01-14T08:00:00Z',
      slaDeadline: '2024-01-21T08:00:00Z',
      slaDays: 5,
      daysRemaining: 3,
      urgency: 'due_this_week',
      assessedPriority: 'High',
    },
    {
      id: 'find-017',
      checkId: 'deep-ai-scan.business-logic-flaw',
      ruleName: 'Business Logic Vulnerability',
      message: 'Price calculation can be manipulated by negative quantity values.',
      filePath: 'src/commerce/cart.py',
      severity: 'high',
      scanType: 'deep_ai_scan',
      applicationId: 'app-killbill',
      applicationName: 'Kill Bill',
      detectedAt: '2024-01-08T12:00:00Z',
      slaDeadline: '2024-01-22T12:00:00Z',
      slaDays: 10,
      daysRemaining: 4,
      urgency: 'due_this_week',
      assessedPriority: 'High',
      isRealConcern: 'Yes',
    },
    {
      id: 'find-018',
      checkId: 'javascript.express.security.audit.express-open-redirect',
      ruleName: 'Open Redirect',
      message: 'User-controlled redirect URL could lead to phishing attacks.',
      filePath: 'src/routes/auth.js',
      severity: 'medium',
      scanType: 'semgrep',
      applicationId: 'app-ghostfolio',
      applicationName: 'Ghostfolio',
      detectedAt: '2024-01-11T16:00:00Z',
      slaDeadline: '2024-01-25T16:00:00Z',
      slaDays: 14,
      daysRemaining: 5,
      urgency: 'due_this_week',
    },
    {
      id: 'find-019',
      checkId: 'ai-security-review.insecure-deserialization',
      ruleName: 'Insecure Deserialization',
      message: 'pickle.loads() on untrusted data can lead to RCE.',
      filePath: 'src/cache/serializer.py',
      severity: 'high',
      scanType: 'deep_ai_scan',
      applicationId: 'app-azuro',
      applicationName: 'Azuro Protocol',
      detectedAt: '2024-01-13T10:00:00Z',
      slaDeadline: '2024-01-22T10:00:00Z',
      slaDays: 7,
      daysRemaining: 4,
      urgency: 'due_this_week',
      assessedPriority: 'Medium',
    },
    {
      id: 'find-020',
      checkId: 'generic.secrets.security.detected-slack-token',
      ruleName: 'Slack Token Exposed',
      message: 'Slack bot token found in source code.',
      filePath: 'src/notifications/slack.ts',
      severity: 'medium',
      scanType: 'secrets',
      applicationId: 'app-onekey',
      applicationName: 'OneKey Wallet',
      detectedAt: '2024-01-14T09:00:00Z',
      slaDeadline: '2024-01-21T09:00:00Z',
      slaDays: 7,
      daysRemaining: 3,
      urgency: 'due_this_week',
    },

    // ========================================================================
    // HEALTHY (27 total) - showing first 7 for brevity
    // ========================================================================
    {
      id: 'find-021',
      checkId: 'python.lang.security.audit.ssl-no-verify',
      ruleName: 'SSL Verification Disabled',
      message: 'SSL certificate verification is disabled, allowing MITM attacks.',
      filePath: 'src/integrations/external_api.py',
      severity: 'medium',
      scanType: 'semgrep',
      applicationId: 'app-stash',
      applicationName: 'Stash',
      detectedAt: '2024-01-15T10:00:00Z',
      slaDeadline: '2024-01-29T10:00:00Z',
      slaDays: 14,
      daysRemaining: 11,
      urgency: 'healthy',
    },
    {
      id: 'find-022',
      checkId: 'deep-ai-scan.race-condition',
      ruleName: 'Potential Race Condition',
      message: 'Database read-modify-write without locking may cause race conditions.',
      filePath: 'src/services/inventory.py',
      severity: 'medium',
      scanType: 'deep_ai_scan',
      applicationId: 'app-killbill',
      applicationName: 'Kill Bill',
      detectedAt: '2024-01-10T14:00:00Z',
      slaDeadline: '2024-02-07T14:00:00Z',
      slaDays: 21,
      daysRemaining: 18,
      urgency: 'healthy',
      assessedPriority: 'Medium',
    },
    {
      id: 'find-023',
      checkId: 'typescript.react.security.audit.react-missing-noopener',
      ruleName: 'Missing noopener',
      message: 'External links should use rel="noopener" to prevent tab nabbing.',
      filePath: 'src/components/ExternalLink.tsx',
      severity: 'low',
      scanType: 'semgrep',
      applicationId: 'app-nautilus',
      applicationName: 'NautilusTrader',
      detectedAt: '2024-01-12T11:00:00Z',
      slaDeadline: '2024-02-11T11:00:00Z',
      slaDays: 30,
      daysRemaining: 24,
      urgency: 'healthy',
    },
    {
      id: 'find-024',
      checkId: 'ai-security-review.weak-random',
      ruleName: 'Weak Random Number Generator',
      message: 'Math.random() is not cryptographically secure. Use crypto.getRandomValues().',
      filePath: 'src/utils/token.ts',
      severity: 'medium',
      scanType: 'deep_ai_scan',
      applicationId: 'app-ghostfolio',
      applicationName: 'Ghostfolio',
      detectedAt: '2024-01-16T08:00:00Z',
      slaDeadline: '2024-01-30T08:00:00Z',
      slaDays: 14,
      daysRemaining: 12,
      urgency: 'healthy',
    },
    {
      id: 'find-025',
      checkId: 'python.lang.security.audit.logging-sensitive-data',
      ruleName: 'Sensitive Data in Logs',
      message: 'Password or token may be logged. Redact sensitive information.',
      filePath: 'src/auth/login.py',
      severity: 'low',
      scanType: 'semgrep',
      applicationId: 'app-stash',
      applicationName: 'Stash',
      detectedAt: '2024-01-14T15:00:00Z',
      slaDeadline: '2024-02-13T15:00:00Z',
      slaDays: 30,
      daysRemaining: 26,
      urgency: 'healthy',
    },
    {
      id: 'find-026',
      checkId: 'deep-ai-scan.insufficient-input-validation',
      ruleName: 'Insufficient Input Validation',
      message: 'User input not validated before processing. Add schema validation.',
      filePath: 'src/api/endpoints/users.py',
      severity: 'medium',
      scanType: 'deep_ai_scan',
      applicationId: 'app-nautilus',
      applicationName: 'NautilusTrader',
      detectedAt: '2024-01-08T09:00:00Z',
      slaDeadline: '2024-02-05T09:00:00Z',
      slaDays: 21,
      daysRemaining: 16,
      urgency: 'healthy',
    },
    {
      id: 'find-027',
      checkId: 'ai-security-review.information-disclosure',
      ruleName: 'Information Disclosure',
      message: 'Stack trace exposed in error response. Use generic error messages.',
      filePath: 'src/middleware/error_handler.py',
      severity: 'medium',
      scanType: 'deep_ai_scan',
      applicationId: 'app-killbill',
      applicationName: 'Kill Bill',
      detectedAt: '2024-01-15T11:00:00Z',
      slaDeadline: '2024-01-29T11:00:00Z',
      slaDays: 14,
      daysRemaining: 11,
      urgency: 'healthy',
    },
  ],

  // Calendar view - next 14 days showing findings due each day
  calendar: [
    // Past days (overdue)
    { date: '2024-01-15', dayOfWeek: 'Mon', isToday: false, isPast: true, isWeekend: false,
      findings: { total: 3, bySeverity: { error: 2, warning: 1, info: 0 } } },
    { date: '2024-01-16', dayOfWeek: 'Tue', isToday: false, isPast: true, isWeekend: false,
      findings: { total: 2, bySeverity: { error: 1, warning: 1, info: 0 } } },
    { date: '2024-01-17', dayOfWeek: 'Wed', isToday: false, isPast: true, isWeekend: false,
      findings: { total: 3, bySeverity: { error: 2, warning: 1, info: 0 } } },
    // Today
    { date: '2024-01-18', dayOfWeek: 'Thu', isToday: true, isPast: false, isWeekend: false,
      findings: { total: 3, bySeverity: { error: 2, warning: 1, info: 0 } } },
    // Tomorrow
    { date: '2024-01-19', dayOfWeek: 'Fri', isToday: false, isPast: false, isWeekend: false,
      findings: { total: 4, bySeverity: { error: 1, warning: 2, info: 1 } } },
    // Weekend
    { date: '2024-01-20', dayOfWeek: 'Sat', isToday: false, isPast: false, isWeekend: true,
      findings: { total: 0, bySeverity: { error: 0, warning: 0, info: 0 } } },
    { date: '2024-01-21', dayOfWeek: 'Sun', isToday: false, isPast: false, isWeekend: true,
      findings: { total: 0, bySeverity: { error: 0, warning: 0, info: 0 } } },
    // Next week
    { date: '2024-01-22', dayOfWeek: 'Mon', isToday: false, isPast: false, isWeekend: false,
      findings: { total: 2, bySeverity: { error: 2, warning: 0, info: 0 } } },
    { date: '2024-01-23', dayOfWeek: 'Tue', isToday: false, isPast: false, isWeekend: false,
      findings: { total: 1, bySeverity: { error: 0, warning: 1, info: 0 } } },
    { date: '2024-01-24', dayOfWeek: 'Wed', isToday: false, isPast: false, isWeekend: false,
      findings: { total: 0, bySeverity: { error: 0, warning: 0, info: 0 } } },
    { date: '2024-01-25', dayOfWeek: 'Thu', isToday: false, isPast: false, isWeekend: false,
      findings: { total: 1, bySeverity: { error: 0, warning: 1, info: 0 } } },
    { date: '2024-01-26', dayOfWeek: 'Fri', isToday: false, isPast: false, isWeekend: false,
      findings: { total: 2, bySeverity: { error: 1, warning: 1, info: 0 } } },
    { date: '2024-01-27', dayOfWeek: 'Sat', isToday: false, isPast: false, isWeekend: true,
      findings: { total: 0, bySeverity: { error: 0, warning: 0, info: 0 } } },
    { date: '2024-01-28', dayOfWeek: 'Sun', isToday: false, isPast: false, isWeekend: true,
      findings: { total: 0, bySeverity: { error: 0, warning: 0, info: 0 } } },
  ],
};

// ============================================================================
// Helper: Get urgency label for display
// ============================================================================

export const getUrgencyLabel = (urgency: Urgency): string => {
  switch (urgency) {
    case 'breached': return 'SLA Breached';
    case 'due_today': return 'Due Today';
    case 'due_tomorrow': return 'Due Tomorrow';
    case 'due_this_week': return 'Due This Week';
    case 'healthy': return 'On Track';
  }
};

export const getUrgencyColor = (urgency: Urgency): { bg: string; text: string; border: string } => {
  switch (urgency) {
    case 'breached':
      return { bg: 'bg-red-500/10', text: 'text-red-500', border: 'border-red-500' };
    case 'due_today':
      return { bg: 'bg-orange-500/10', text: 'text-orange-500', border: 'border-orange-500' };
    case 'due_tomorrow':
      return { bg: 'bg-amber-500/10', text: 'text-amber-500', border: 'border-amber-500' };
    case 'due_this_week':
      return { bg: 'bg-yellow-500/10', text: 'text-yellow-500', border: 'border-yellow-500' };
    case 'healthy':
      return { bg: 'bg-brand-primary/10', text: 'text-brand-primary', border: 'border-brand-primary' };
  }
};
